import "./App.css";
import { useEffect, useState } from "react";


/* ==========================================================
   CUSTOM YOGHURT CUP
========================================================== */

function CupIllustration({
  levels,
  getFruitColor,
  honey,
  granola
}) {

  /*
    SVG dimensions:
    300 x 440

    Cup internal body:
    y = 78 -> 405
  */

  const bodyTop = 78;
  const bodyBottom = 405;


  /*
    White yoghurt at the very top.
  */

  const topYogurtHeight = 45;


  /*
    Fruit starts underneath the top yoghurt.
  */

  const fruitAreaTop =
    bodyTop + topYogurtHeight;


  /*
    White yoghurt between fruit layers.
  */

  const yogurtGap = 22;


  /*
    Granola is NOT a fruit level.

    When selected it gets its own fixed area
    at the bottom of the cup.
  */

  const granolaHeight = 58;

  const granolaGap =
    granola && levels.length > 0
      ? 18
      : 0;

  const granolaY =
    bodyBottom - granolaHeight;


  /*
    Fruit must stop above the granola.
  */

  const fruitBottom =
    granola
      ? granolaY - granolaGap
      : bodyBottom;


  /*
    How wavy the yoghurt is.
  */

  const waveAmplitude = 8;


  const numberOfLevels =
    levels.length;


  /*
    IMPORTANT:

    Reverse the visual order in the cup.

    User selects:

    Level 1
    Level 2
    Level 3

    Cup shows:

       TOP

       Level 1

       yoghurt

       Level 2

       yoghurt

       Level 3

       yoghurt

       Granola

       BOTTOM
  */

  const cupLevels =
    [...levels].reverse();


  /*
    Each selected fruit gets equal height.

    If Granola is selected, the fruit area
    becomes smaller because Granola occupies
    the bottom.
  */

  const levelHeight =
    numberOfLevels > 0
      ? (
          fruitBottom -
          fruitAreaTop -
          yogurtGap *
            (numberOfLevels - 1)
        ) / numberOfLevels
      : 0;



  /* ======================================================
     CREATE WAVY YOGHURT BAND
  ====================================================== */

  const makeYogurtBand =
    (y, height) => {

      const a =
        waveAmplitude;


      return `
        M 0 ${y}

        Q 25 ${y - a}
          50 ${y}

        Q 75 ${y + a}
          100 ${y}

        Q 125 ${y - a}
          150 ${y}

        Q 175 ${y + a}
          200 ${y}

        Q 225 ${y - a}
          250 ${y}

        Q 275 ${y + a}
          300 ${y}


        L 300 ${y + height}


        Q 275 ${y + height - a}
          250 ${y + height}

        Q 225 ${y + height + a}
          200 ${y + height}

        Q 175 ${y + height - a}
          150 ${y + height}

        Q 125 ${y + height + a}
          100 ${y + height}

        Q 75 ${y + height - a}
          50 ${y + height}

        Q 25 ${y + height + a}
          0 ${y + height}

        Z
      `;
    };



  /* ======================================================
     TOP YOGHURT

     Wavy on the bottom.
  ====================================================== */

  const makeTopYogurt =
    () => {

      const y =
        fruitAreaTop;

      const a =
        waveAmplitude;


      return `
        M 0 ${bodyTop}

        L 300 ${bodyTop}

        L 300 ${y}

        Q 275 ${y + a}
          250 ${y}

        Q 225 ${y - a}
          200 ${y}

        Q 175 ${y + a}
          150 ${y}

        Q 125 ${y - a}
          100 ${y}

        Q 75 ${y + a}
          50 ${y}

        Q 25 ${y - a}
          0 ${y}

        Z
      `;
    };



  return (

    <svg
      className="yogurt-cup-svg"
      viewBox="0 0 300 440"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >

      <defs>


        {/* ==================================================
            EXACT INTERNAL CUP SHAPE
        ================================================== */}

        <clipPath id="cupBodyClip">

          <path
            d="
              M 50 78

              L 250 78

              L 222 386

              Q 220 410
                198 416

              Q 150 427
                102 416

              Q 80 410
                78 386

              Z
            "
          />

        </clipPath>



        {/* ==================================================
            TRANSPARENT PLASTIC
        ================================================== */}

        <linearGradient
          id="plasticGradient"
          x1="0"
          y1="0"
          x2="1"
          y2="0"
        >

          <stop
            offset="0%"
            stopColor="#ffffff"
            stopOpacity="0.68"
          />

          <stop
            offset="18%"
            stopColor="#ffffff"
            stopOpacity="0.10"
          />

          <stop
            offset="50%"
            stopColor="#ffffff"
            stopOpacity="0.02"
          />

          <stop
            offset="82%"
            stopColor="#ffffff"
            stopOpacity="0.08"
          />

          <stop
            offset="100%"
            stopColor="#ffffff"
            stopOpacity="0.60"
          />

        </linearGradient>



        {/* ==================================================
            HONEY
        ================================================== */}

        <linearGradient
          id="honeyGradient"
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >

          <stop
            offset="0%"
            stopColor="#ffdc65"
          />

          <stop
            offset="45%"
            stopColor="#f3ad13"
          />

          <stop
            offset="100%"
            stopColor="#d98500"
          />

        </linearGradient>



        {/* ==================================================
            GRANOLA PATTERN
        ================================================== */}

        <pattern
          id="granolaPattern"
          width="32"
          height="24"
          patternUnits="userSpaceOnUse"
        >

          {/* GRANOLA BACKGROUND */}

          <rect
            width="32"
            height="24"
            fill="#c78b3b"
          />


          {/* OATS */}

          <ellipse
            cx="5"
            cy="6"
            rx="4.5"
            ry="2.5"
            fill="#e7bd73"
            transform="rotate(-20 5 6)"
          />

          <ellipse
            cx="17"
            cy="5"
            rx="5"
            ry="2.5"
            fill="#9d642d"
            transform="rotate(17 17 5)"
          />

          <ellipse
            cx="27"
            cy="13"
            rx="4.5"
            ry="2.4"
            fill="#e2ae5d"
            transform="rotate(-15 27 13)"
          />


          {/* GRANOLA PIECES */}

          <circle
            cx="10"
            cy="17"
            r="3.5"
            fill="#885126"
          />

          <circle
            cx="21"
            cy="19"
            r="2.8"
            fill="#f0c97f"
          />

          <ellipse
            cx="29"
            cy="4"
            rx="2.5"
            ry="1.4"
            fill="#f4d496"
          />

          <ellipse
            cx="3"
            cy="22"
            rx="3"
            ry="1.5"
            fill="#a86831"
          />

        </pattern>



        {/* ==================================================
            CUP SHADOW
        ================================================== */}

        <filter
          id="cupShadow"
          x="-30%"
          y="-30%"
          width="160%"
          height="160%"
        >

          <feDropShadow
            dx="0"
            dy="6"
            stdDeviation="6"
            floodColor="#000000"
            floodOpacity="0.14"
          />

        </filter>


      </defs>



      {/* ==================================================
          SHADOW UNDER CUP
      ================================================== */}

      <ellipse
        cx="150"
        cy="424"
        rx="83"
        ry="10"
        fill="rgba(0,0,0,0.10)"
      />



      {/* ==================================================
          CUP CONTENTS
      ================================================== */}

      <g clipPath="url(#cupBodyClip)">


        {/* WHITE YOGHURT BACKGROUND */}

        <rect
          x="0"
          y={bodyTop}
          width="300"
          height={
            bodyBottom -
            bodyTop
          }
          fill="#fffefa"
        />



        {/* ==================================================
            FRUIT LEVELS
        ================================================== */}

        {cupLevels.map(
          (level, index) => {

            /*
              cupLevels is reversed.

              Therefore Level 1
              remains visually at the top.
            */

            const y =
              fruitBottom -
              levelHeight *
                (index + 1) -
              yogurtGap *
                index;


            return (

              <g
                key={
                  `${level.number}-${level.value}`
                }
              >


                {/* FRUIT COLOR FROM API */}

                <rect
                  x="0"
                  y={y}
                  width="300"
                  height={levelHeight}
                  fill={
                    getFruitColor(
                      level.value
                    )
                  }
                />



                {/* LIGHT ON TOP */}

                <rect
                  x="0"
                  y={y}
                  width="300"
                  height={
                    levelHeight *
                    0.16
                  }
                  fill="rgba(255,255,255,0.13)"
                />



                {/* LOWER SHADING */}

                <rect
                  x="0"
                  y={
                    y +
                    levelHeight *
                    0.84
                  }
                  width="300"
                  height={
                    levelHeight *
                    0.16
                  }
                  fill="rgba(0,0,0,0.025)"
                />


              </g>

            );

          }
        )}



        {/* ==================================================
            WAVY WHITE YOGHURT BETWEEN FRUIT LEVELS
        ================================================== */}

        {cupLevels
          .slice(0, -1)
          .map(
            (level, index) => {

              const lowerFruitTop =
                fruitBottom -
                levelHeight *
                  (index + 1) -
                yogurtGap *
                  index;


              const gapTop =
                lowerFruitTop -
                yogurtGap;


              return (

                <g
                  key={
                    `yogurt-gap-${index}`
                  }
                >


                  {/* WAVY WHITE BAND */}

                  <path
                    d={
                      makeYogurtBand(
                        gapTop,
                        yogurtGap
                      )
                    }
                    fill="#fffefa"
                  />



                  {/* HIGHLIGHT */}

                  <path
                    d={`
                      M 0 ${gapTop + 5}

                      Q 25 ${gapTop - 1}
                        50 ${gapTop + 5}

                      Q 75 ${gapTop + 11}
                        100 ${gapTop + 5}

                      Q 125 ${gapTop - 1}
                        150 ${gapTop + 5}

                      Q 175 ${gapTop + 11}
                        200 ${gapTop + 5}

                      Q 225 ${gapTop - 1}
                        250 ${gapTop + 5}

                      Q 275 ${gapTop + 11}
                        300 ${gapTop + 5}
                    `}
                    fill="none"
                    stroke="rgba(255,255,255,0.95)"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />


                </g>

              );

            }
          )}



        {/* ==================================================
            YOGHURT BETWEEN FRUIT AND GRANOLA
        ================================================== */}

        {
          granola &&
          numberOfLevels > 0 &&
          granolaGap > 0 && (

            <g>

              <path
                d={
                  makeYogurtBand(
                    fruitBottom,
                    granolaGap
                  )
                }
                fill="#fffefa"
              />


              <path
                d={`
                  M 0 ${fruitBottom + 4}

                  Q 25 ${fruitBottom - 2}
                    50 ${fruitBottom + 4}

                  Q 75 ${fruitBottom + 10}
                    100 ${fruitBottom + 4}

                  Q 125 ${fruitBottom - 2}
                    150 ${fruitBottom + 4}

                  Q 175 ${fruitBottom + 10}
                    200 ${fruitBottom + 4}

                  Q 225 ${fruitBottom - 2}
                    250 ${fruitBottom + 4}

                  Q 275 ${fruitBottom + 10}
                    300 ${fruitBottom + 4}
                `}
                fill="none"
                stroke="rgba(255,255,255,0.95)"
                strokeWidth="3"
                strokeLinecap="round"
              />

            </g>

          )
        }



        {/* ==================================================
            GRANOLA AT BOTTOM
        ================================================== */}

        {granola && (

          <g>

            <rect
              x="0"
              y={granolaY}
              width="300"
              height={
                granolaHeight + 20
              }
              fill="url(#granolaPattern)"
            />


            {/* BIGGER GRANOLA PIECES */}

            <ellipse
              cx="85"
              cy={granolaY + 18}
              rx="12"
              ry="6"
              fill="#e7b467"
              transform={
                `rotate(-15 85 ${granolaY + 18})`
              }
            />

            <ellipse
              cx="124"
              cy={granolaY + 39}
              rx="10"
              ry="5"
              fill="#915724"
              transform={
                `rotate(14 124 ${granolaY + 39})`
              }
            />

            <circle
              cx="158"
              cy={granolaY + 20}
              r="7"
              fill="#e6b66e"
            />

            <ellipse
              cx="196"
              cy={granolaY + 38}
              rx="11"
              ry="5"
              fill="#a9672d"
              transform={
                `rotate(-16 196 ${granolaY + 38})`
              }
            />

            <circle
              cx="225"
              cy={granolaY + 18}
              r="6"
              fill="#efca86"
            />



            {/* TOP GRANOLA HIGHLIGHT */}

            <path
              d={`
                M 0 ${granolaY + 5}

                Q 30 ${granolaY - 2}
                  60 ${granolaY + 5}

                Q 90 ${granolaY + 12}
                  120 ${granolaY + 5}

                Q 150 ${granolaY - 2}
                  180 ${granolaY + 5}

                Q 210 ${granolaY + 12}
                  240 ${granolaY + 5}

                Q 270 ${granolaY - 2}
                  300 ${granolaY + 5}
              `}
              fill="none"
              stroke="#efd198"
              strokeWidth="3"
              opacity="0.75"
            />

          </g>

        )}



        {/* ==================================================
            WHITE YOGHURT ABOVE THE TOP FRUIT
        ================================================== */}

        <path
          d={makeTopYogurt()}
          fill="#fffefa"
        />



        {/* WAVE SHADOW */}

        <path
          d={`
            M 0 ${fruitAreaTop - 1}

            Q 25 ${fruitAreaTop + 7}
              50 ${fruitAreaTop}

            Q 75 ${fruitAreaTop - 7}
              100 ${fruitAreaTop}

            Q 125 ${fruitAreaTop + 7}
              150 ${fruitAreaTop}

            Q 175 ${fruitAreaTop - 7}
              200 ${fruitAreaTop}

            Q 225 ${fruitAreaTop + 7}
              250 ${fruitAreaTop}

            Q 275 ${fruitAreaTop - 7}
              300 ${fruitAreaTop}
          `}
          fill="none"
          stroke="#eeeae2"
          strokeWidth="3"
        />


      </g>



      {/* ==================================================
          TRANSPARENT CUP BODY
      ================================================== */}

      <path
        d="
          M 50 78

          L 250 78

          L 222 386

          Q 220 410
            198 416

          Q 150 427
            102 416

          Q 80 410
            78 386

          Z
        "
        fill="url(#plasticGradient)"
        stroke="#cbd5dd"
        strokeWidth="3"
        filter="url(#cupShadow)"
      />



      {/* ==================================================
          LEFT PLASTIC REFLECTION
      ================================================== */}

      <path
        d="
          M 69 110
          L 93 370
        "
        fill="none"
        stroke="white"
        strokeWidth="7"
        strokeLinecap="round"
        opacity="0.28"
      />



      {/* ==================================================
          RIGHT PLASTIC REFLECTION
      ================================================== */}

      <path
        d="
          M 231 110
          L 211 365
        "
        fill="none"
        stroke="white"
        strokeWidth="4"
        strokeLinecap="round"
        opacity="0.18"
      />



      {/* ==================================================
          YOGHURT AT OPEN TOP
      ================================================== */}

      <ellipse
        cx="150"
        cy="77"
        rx="94"
        ry="17"
        fill="#fffefa"
        stroke="#e5e1d8"
        strokeWidth="2"
      />



      {/* ==================================================
          YOGHURT WAVES AT TOP
      ================================================== */}

      <path
        d="
          M 70 77

          Q 88 66
            108 76

          Q 128 87
            148 76

          Q 168 66
            188 76

          Q 208 86
            230 76
        "
        fill="none"
        stroke="#eee9df"
        strokeWidth="3.5"
        strokeLinecap="round"
      />


      <path
        d="
          M 95 82

          Q 113 73
            132 81

          Q 152 89
            172 80

          Q 190 72
            207 81
        "
        fill="none"
        stroke="#f5f1e9"
        strokeWidth="2.5"
        strokeLinecap="round"
      />



      {/* ==================================================
          HONEY ON TOP OF YOGHURT
      ================================================== */}

      {honey && (

        <g>


          {/* MAIN HONEY SWIRL */}

          <path
            d="
              M 82 76

              C 100 64
                119 85
                140 76

              C 160 67
                175 62
                194 73

              C 208 82
                221 79
                229 73
            "
            fill="none"
            stroke="url(#honeyGradient)"
            strokeWidth="12"
            strokeLinecap="round"
            strokeLinejoin="round"
          />



          {/* SECOND HONEY SWIRL */}

          <path
            d="
              M 101 85

              C 121 73
                141 90
                161 82

              C 180 74
                195 80
                211 84
            "
            fill="none"
            stroke="#e79a05"
            strokeWidth="7"
            strokeLinecap="round"
          />



          {/* HONEY HIGHLIGHT */}

          <path
            d="
              M 96 71

              Q 127 65
                158 73
            "
            fill="none"
            stroke="rgba(255,255,255,0.50)"
            strokeWidth="2.5"
            strokeLinecap="round"
          />



          <circle
            cx="220"
            cy="82"
            r="5"
            fill="#e69b08"
          />


        </g>

      )}



      {/* ==================================================
          TOP CUP RIM
      ================================================== */}

      <ellipse
        cx="150"
        cy="77"
        rx="108"
        ry="24"
        fill="none"
        stroke="#cbd5dd"
        strokeWidth="4"
      />



      <path
        d="
          M 63 72

          Q 150 46
            237 72
        "
        fill="none"
        stroke="white"
        strokeWidth="4"
        strokeLinecap="round"
        opacity="0.75"
      />



      {/* ==================================================
          BOTTOM RING
      ================================================== */}

      <ellipse
        cx="150"
        cy="401"
        rx="71"
        ry="17"
        fill="rgba(255,255,255,0.10)"
        stroke="rgba(188,200,208,0.65)"
        strokeWidth="2"
      />


    </svg>

  );

}



/* ==========================================================
   MAIN APP
========================================================== */

function App() {

  const [open, setOpen] =
    useState(false);


  /*
    foodlist contains objects:

    {
      name: "Μήλο",
      color: "#91C553"
    }
  */

  const [submitting, setSubmitting] =
    useState(false);

  const [submitMessage, setSubmitMessage] =
    useState("");


  const [foodlist, setFoodlist] =
    useState([]);



  /* ======================================================
     THREE FRUIT LEVELS ONLY
  ====================================================== */

  const [value1, setValue1] =
    useState("Τίποτα");


  const [value2, setValue2] =
    useState("Τίποτα");


  const [value3, setValue3] =
    useState("Τίποτα");



  /* ======================================================
     OPTIONAL EXTRAS
  ====================================================== */

  const [granola, setGranola] =
    useState(false);


  const [honey, setHoney] =
    useState(false);



  /* ======================================================
     GET FOOD LIST FROM EXPRESS
  ====================================================== */

  useEffect(() => {

    const fetchData =
      async () => {

        try {

          const response =
            await fetch(
              "/api/foodlist"
            );


          if (!response.ok) {

            throw new Error(
              `HTTP error ${response.status}`
            );

          }


          const data =
            await response.json();


          console.log(
            "Food list:",
            data
          );


          setFoodlist(data);

        }

        catch (error) {

          console.error(
            "Error fetching food list:",
            error
          );

        }

      };


    fetchData();

  }, []);



  /* ======================================================
     SUBMIT ORDER
  ====================================================== */

  const handleSubmit =
    async () => {

      /*
        Granola is now independent,
        exactly like Honey.
      */

      const order = {

        levels: [

          {
            number: 1,
            food:
              value1 === "Τίποτα"
                ? null
                : value1
          },

          {
            number: 2,
            food:
              value2 === "Τίποτα"
                ? null
                : value2
          },

          {
            number: 3,
            food:
              value3 === "Τίποτα"
                ? null
                : value3
          }

        ],


        granola:
          granola,


        honey:
          honey

      };


      try {

        setSubmitting(true);

        setSubmitMessage("");


        const response =
          await fetch(
            "/api/order",
            {

              method:
                "POST",

              headers: {
                "Content-Type":
                  "application/json"
              },

              body:
                JSON.stringify(
                  order
                )

            }
          );


        if (!response.ok) {

          throw new Error(
            `HTTP error ${response.status}`
          );

        }


        const data =
          await response.json();


        console.log(
          "Server response:",
          data
        );


        setSubmitMessage(
          "Η παραγγελία στάλθηκε!"
        );

      }

      catch (error) {

        console.error(
          "Error sending order:",
          error
        );


        setSubmitMessage(
          "Αποτυχία αποστολής."
        );

      }

      finally {

        setSubmitting(false);

      }

    };



  /* ======================================================
     SELECTED FRUIT LEVELS
  ====================================================== */

  const selectedLevels = [

    {
      number: 1,
      value: value1
    },

    {
      number: 2,
      value: value2
    },

    {
      number: 3,
      value: value3
    }

  ].filter(
    (level) =>
      level.value !==
      "Τίποτα"
  );



  /* ======================================================
     GET COLOR FROM API FOODLIST
  ====================================================== */

  const getFruitColor =
    (fruitName) => {

      const food =
        foodlist.find(
          (item) =>
            item.name ===
            fruitName
        );


      if (food) {

        return food.color;

      }


      return "#CCCCCC";

    };



  return (

    <div className="main">


      {/* ==================================================
          HEADER
      ================================================== */}

      <div className="header bg-white-500">

        <p
          id="title"
          className="font-mono antialiased"
        >
          My Giaourti
        </p>

      </div>



      {/* ==================================================
          CHOICES
      ================================================== */}

      <div className="choices bg-white-500">


        <button
          id="levels"
          className="levels-button"
          onClick={() =>
            setOpen(
              !open
            )
          }
        >

          <span>
            Επίπεδα
          </span>


          <span
            className={
              open
                ? "dropdown-arrow dropdown-arrow-open"
                : "dropdown-arrow"
            }
          >
            ▼
          </span>

        </button>



        {/* ==================================================
            DROPDOWN
        ================================================== */}

        {open && (

          <div className="levels-dropdown">


            {/* ==================================================
                LEVEL 1
            ================================================== */}

            <div className="choicediv">

              <label htmlFor="level1">
                Επίπεδο 1
              </label>


              <select
                id="level1"
                value={value1}
                onChange={
                  (e) =>
                    setValue1(
                      e.target.value
                    )
                }
              >

                <option value="Τίποτα">
                  Τίποτα
                </option>


                {foodlist.map(
                  (food, index) => (

                    <option
                      key={
                        `${food.name}-1-${index}`
                      }
                      value={
                        food.name
                      }
                    >
                      {food.name}
                    </option>

                  )
                )}

              </select>

            </div>



            {/* ==================================================
                LEVEL 2
            ================================================== */}

            <div className="choicediv">

              <label htmlFor="level2">
                Επίπεδο 2
              </label>


              <select
                id="level2"
                value={value2}
                onChange={
                  (e) =>
                    setValue2(
                      e.target.value
                    )
                }
              >

                <option value="Τίποτα">
                  Τίποτα
                </option>


                {foodlist.map(
                  (food, index) => (

                    <option
                      key={
                        `${food.name}-2-${index}`
                      }
                      value={
                        food.name
                      }
                    >
                      {food.name}
                    </option>

                  )
                )}

              </select>

            </div>



            {/* ==================================================
                LEVEL 3
            ================================================== */}

            <div className="choicediv">

              <label htmlFor="level3">
                Επίπεδο 3
              </label>


              <select
                id="level3"
                value={value3}
                onChange={
                  (e) =>
                    setValue3(
                      e.target.value
                    )
                }
              >

                <option value="Τίποτα">
                  Τίποτα
                </option>


                {foodlist.map(
                  (food, index) => (

                    <option
                      key={
                        `${food.name}-3-${index}`
                      }
                      value={
                        food.name
                      }
                    >
                      {food.name}
                    </option>

                  )
                )}

              </select>

            </div>



            {/* ==================================================
                GRANOLA
            ================================================== */}

            <div className="honey-choice">

              <label htmlFor="granola">
                Granola
              </label>


              <input
                type="checkbox"
                id="granola"
                checked={granola}
                onChange={
                  (e) =>
                    setGranola(
                      e.target.checked
                    )
                }
              />

            </div>



            {/* ==================================================
                HONEY
            ================================================== */}

            <div className="honey-choice">

              <label htmlFor="meli">
                Μέλι
              </label>


              <input
                type="checkbox"
                id="meli"
                checked={honey}
                onChange={
                  (e) =>
                    setHoney(
                      e.target.checked
                    )
                }
              />

            </div>


          </div>

        )}


      </div>



      {/* ==================================================
          BOTTOM CUP AREA
      ================================================== */}

      <div className="cup bg-white-500">


        {/* ==================================================
            LEFT SIDE - CUP
        ================================================== */}

        <div className="cup-left">

          <CupIllustration

            levels={
              selectedLevels
            }

            getFruitColor={
              getFruitColor
            }

            granola={
              granola
            }

            honey={
              honey
            }

          />

        </div>



        {/* ==================================================
            RIGHT SIDE - INFORMATION
        ================================================== */}

        <div className="cup-levels-info">


          {/* NOTHING SELECTED */}

          {
            selectedLevels.length === 0 &&
            !granola &&
            !honey
              ? (

                <div className="empty-selection">

                  <div>

                  </div>

                </div>

              )
              : (

                <>

                  {/* ==========================================
                      FRUIT LEVELS
                  ========================================== */}

                  {selectedLevels.map(
                    (level) => (

                      <div
                        className="cup-info-row"
                        key={
                          level.number
                        }
                      >


                        {/* FRUIT COLOR FROM API */}

                        <span
                          className="fruit-dot"
                          style={{
                            backgroundColor:
                              getFruitColor(
                                level.value
                              )
                          }}
                        />


                        <div className="cup-info-text">

                          <small>
                            Επίπεδο{" "}
                            {level.number}
                          </small>


                          <strong>
                            {level.value}
                          </strong>

                        </div>


                      </div>

                    )
                  )}



                  {/* ==========================================
                      GRANOLA INFORMATION
                  ========================================== */}

                  {granola && (

                    <div className="honey-info-row">

                      <span
                        className="fruit-dot"
                        style={{
                          backgroundColor:
                            "#c78b3b"
                        }}
                      />


                      <div className="cup-info-text">

                        <small>
                          Έξτρα
                        </small>


                        <strong>
                          Granola
                        </strong>

                      </div>

                    </div>

                  )}



                  {/* ==========================================
                      HONEY INFORMATION
                  ========================================== */}

                  {honey && (

                    <div className="honey-info-row">

                      <span className="honey-dot" />


                      <div className="cup-info-text">

                        <small>
                          Έξτρα
                        </small>


                        <strong>
                          Μέλι
                        </strong>

                      </div>

                    </div>

                  )}


                </>

              )
          }



          {/* ==================================================
              YOGHURT INFORMATION
          ================================================== */}

          {selectedLevels.length > 1 && (

            <div className="yoghurt-info">

            </div>

          )}


        </div>


      </div>



      {/* ==================================================
          SUBMIT ORDER
      ================================================== */}

      <div className="submit-section">

        <button
          className="submit-button"
          onClick={
            handleSubmit
          }
          disabled={
            submitting
          }
        >

          {
            submitting
              ? "Αποστολή..."
              : "Υποβολή Παραγγελίας"
          }

        </button>


        {submitMessage && (

          <p className="submit-message">

            {submitMessage}

          </p>

        )}

      </div>


    </div>

  );

}

export default App;